/**
 * Project Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between Project TypeScript type and Firestore data.
 *
 * Based on schema-generated converter but adapted for client SDK.
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { Project } from '@segmently/schema-models/entities';

export const projectConverter: FirestoreDataConverter<Project> = {
  /**
   * Convert Project model to Firestore data
   * Called when writing to Firestore
   */
  toFirestore(project: Project): any {
    const result: any = { ...project };

    // Convert ISO string dates to Firestore Timestamps
    if (typeof result['creationDate'] === 'string') {
      result['creationDate'] = Timestamp.fromDate(new Date(result['creationDate']));
    } else if (result['creationDate'] instanceof Date) {
      result['creationDate'] = Timestamp.fromDate(result['creationDate']);
    }

    if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = Timestamp.fromDate(new Date(result['updatedAt']));
    } else if (result['updatedAt'] instanceof Date) {
      result['updatedAt'] = Timestamp.fromDate(result['updatedAt']);
    }

    return result;
  },

  /**
   * Convert Firestore data to Project model
   * Called when reading from Firestore
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Project {
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Convert Firestore Timestamps to Date
    if (result['creationDate']?.toDate) {
      result['creationDate'] = result['creationDate'].toDate();
    } else if (typeof result['creationDate'] === 'string') {
      result['creationDate'] = new Date(result['creationDate']);
    }

    if (result['updatedAt']?.toDate) {
      result['updatedAt'] = result['updatedAt'].toDate();
    } else if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = new Date(result['updatedAt']);
    }

    return result as Project;
  }
};

export default projectConverter;
